/* ---------------------------------------------------------------------------
   STL 2026 — 4.11  Header arrows + horizontal paging for the three card rows

   Serena, 3 Sep 2026. All three section headers draw a prev/next pair at the
   right (`5541:14423` / `5541:14293` desktop, `5494:12536` / `5494:12637`
   mobile), and the mobile frames show ONE ROW of cards rather than the 2 x 2 grid
   Madrid renders — the rest are meant to be paged, not wrapped.

   WHY NO LIBRARY. All three tracks are, or can trivially become, native
   horizontal scrollers:

     You May Also Like   a `<ul>` grid  -> flex + overflow-x in custom.css
     Recently Viewed     the same
     As Seen On          Foursixty's `.fs-wrapper` ALREADY has overflow-x: scroll

   So one `scrollBy()` drives all three, touch-drag and trackpad swipe come free,
   and there is no second slider engine to fight — which matters for As Seen On,
   where Foursixty owns the markup. Swiper would have meant re-templating two
   Madrid sections for behaviour the browser already provides.

   Foursixty renders its own prev/next inside the slider; those stay hidden in
   CSS. Ours live in the header where the frames put them, and drive the same
   element.
   ------------------------------------------------------------------------- */

(function () {
  'use strict';

  /* Loaded from TWO places: the footer section (every page, 11 Sep 2026 — the
     homepage Popular Products row needs it) and the PDP's custom_liquid block in
     product.json, which predates that. Everything below is idempotent anyway, but
     one run is cleaner than relying on that. */
  if (window.stlCarouselLoaded) return;
  window.stlCarouselLoaded = true;

  /* Each entry: the section, the element that scrolls, and the row the arrows
     are appended to. Kept as data so a fourth carousel is one line. */
  var TARGETS = [
    {
      section: '[id$="__product-recommendations"]',
      track: '.product-recommendations__wrapper',
      head: '.section-heading'
    },
    {
      /* Any Madrid Products grid section too — same markup as Recently Viewed
         (About page, 11 Sep 2026). */
      section: '[id$="__recently_viewed"], [id*="__products_grid"]',
      track: '.products-grid-section__wrapper',
      head: '.section-heading'
    },
    {
      /* Homepage Popular Products (11 Sep 2026) — the same row as You May Also
         Like. Arrows need a heading row to live in, so they appear once the
         section's own Heading is set; without one the row still scrolls by
         touch and trackpad. */
      section: '[id$="__popular-products"]',
      track: '.popular-products__wrapper',
      head: '.section-heading'
    },
    {
      section: '.stl-asseenon',
      track: '.fs-wrapper',
      head: '.stl-asseenon__head'
    }
  ];

  function build(section, track, head) {
    if (head.querySelector('.stl-carousel-nav')) return;

    var nav = document.createElement('div');
    nav.className = 'stl-carousel-nav';
    nav.innerHTML =
      '<button type="button" class="stl-carousel-nav__btn" data-dir="prev" aria-label="Previous"></button>' +
      '<button type="button" class="stl-carousel-nav__btn" data-dir="next" aria-label="Next"></button>';
    head.appendChild(nav);

    var prev = nav.querySelector('[data-dir="prev"]');
    var next = nav.querySelector('[data-dir="next"]');

    /* A page is one viewport of the track, so the arrows advance by what you can
       see rather than by a card — which keeps 4-up and 2-up feeling the same. */
    function page(dir) {
      track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
    }

    prev.addEventListener('click', function () { page(-1); });
    next.addEventListener('click', function () { page(1); });

    /* The whole nav disappears when there is nothing to page — four products in a
       four-up row, or a visitor with two items of history. The frames only ever
       draw the arrows against an overflowing row, and a dead control is worse
       than none. */
    function sync() {
      var overflow = track.scrollWidth - track.clientWidth;
      nav.hidden = overflow <= 1;
      if (nav.hidden) return;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= overflow - 1;
    }

    sync();
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    /* The first `sync()` almost always runs too early — Foursixty fills its tiles
       after the fact, and Madrid injects both recommendation lists on
       intersection — so the track is watched two ways, and BOTH are needed:

         ResizeObserver     the track's own box changing
         MutationObserver   its CONTENT changing

       The second is the one that is easy to miss. A track can gain six tiles
       without changing size at all — it is full-bleed, so its width is the
       viewport either way — and only `scrollWidth` moves. ResizeObserver does not
       fire for that, which left As Seen On pageable on mobile with its arrows
       still hidden. */
    if (window.ResizeObserver) new ResizeObserver(sync).observe(track);
    if (window.MutationObserver) {
      new MutationObserver(sync).observe(track, { childList: true, subtree: true });
    }
  }

  /* THE TRACK USUALLY DOES NOT EXIST YET, and timers are not a safe way to wait
     for it. All three of these sections fill themselves in after page load —
     Madrid fetches both recommendation lists on intersection, and Foursixty
     injects its slider when its own script resolves — so a section can sit empty
     for an unpredictable length of time. A first pass on DOMContentLoaded plus a
     couple of `setTimeout` retries missed You May Also Like entirely: it arrived
     after the last retry, and the row scrolled with no arrows on it.

     Watching each section for its track to appear is deterministic instead of
     hopeful.

     THE OBSERVER IS NEVER DISCONNECTED, deliberately. Madrid replaces the whole
     of `product-recommendations`' inner HTML when it injects — heading, container
     and all — so a nav built against earlier markup is thrown away with it, and a
     one-shot observer leaves the row scrollable with no arrows. `build()` already
     no-ops when a nav is present, so re-running is free and the section can be
     re-rendered any number of times. */
  function attach(section, t) {
    var track = section.querySelector(t.track);
    var head = section.querySelector(t.head);
    if (track && head) {
      build(section, track, head);
      return true;
    }
    return false;
  }

  function init() {
    TARGETS.forEach(function (t) {
      Array.prototype.forEach.call(
        document.querySelectorAll(t.section),
        function (section) {
          attach(section, t);

          /* The observer is registered EVEN WHEN THE FIRST ATTACH SUCCEEDS.
             Returning early on success looks like an optimisation and is a bug:
             Recently Viewed renders server-side, so it attaches immediately and
             skipped the observer — then Madrid replaced the section's inner HTML
             and the nav went with it, permanently. Everything here is idempotent,
             so registering unconditionally costs one observer and removes a whole
             class of ordering problem. */
          if (!window.MutationObserver) return;
          if (section.dataset.stlCarousel) return;
          section.dataset.stlCarousel = '1';
          new MutationObserver(function () {
            attach(section, t);
          }).observe(section, { childList: true, subtree: true });
        }
      );
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Section reload in the theme editor replaces the markup wholesale. */
  document.addEventListener('shopify:section:load', init);
})();
